import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Check, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const req = (method, path, data) => axios({ method, url: `${API}${path}`, data, withCredentials: true });

const input =
  "w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#06B6D4]/30";
const btnDark =
  "inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-4 py-2 text-xs font-semibold";
const btnGhost =
  "inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-white/10 px-3 py-1.5 text-xs";

const EMPTY = {
  name: "",
  email: "",
  role: "",
  package_id: "premium",
  rating: 5,
  text: "",
  video_link: "",
  poster: "",
  status: "published",
};

function TestimonialForm({ initial, tiers, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const doPreview = async () => {
    try {
      const { data } = await req("post", "/admin/drive/validate", { link: form.video_link });
      setPreview(data.embed_url);
      toast.success("Drive link is valid");
    } catch (err) {
      setPreview(null);
      toast.error(err?.response?.data?.detail || "Invalid Drive link");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        name: form.name,
        email: form.email || null,
        role: form.role,
        package_id: form.package_id,
        rating: Number(form.rating),
        text: form.text,
        video_link: form.video_link || null,
        poster: form.poster || null,
        status: form.status,
      };
      if (initial?.id) await req("put", `/admin/testimonials/${initial.id}`, body);
      else await req("post", "/admin/testimonials", body);
      toast.success(initial?.id ? "Testimonial updated" : "Testimonial added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save testimonial");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="testimonial-form"
      className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] p-5 space-y-3 max-w-2xl"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold tracking-tight">
          {initial?.id ? "Edit testimonial" : "Add new testimonial"}
        </h3>
        <button type="button" onClick={onCancel} className={btnGhost}>
          <X className="h-3 w-3" />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <input required minLength={2} data-testid="testimonial-name" className={input} placeholder="Student name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input type="email" data-testid="testimonial-email" className={input} placeholder="Student email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input data-testid="testimonial-role" className={input} placeholder="Role (e.g. College Student)" value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} />
        <select data-testid="testimonial-package" className={input} value={form.package_id || ""} onChange={(e) => setForm({ ...form, package_id: e.target.value })}>
          {Object.entries(tiers || {}).map(([id, t]) => (
            <option key={id} value={id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Rating</span>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setForm({ ...form, rating: n })}
            data-testid={`testimonial-rating-${n}`}
            aria-label={`${n} star`}
          >
            <Star className={`h-4 w-4 ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>

      <textarea rows={3} maxLength={1200} data-testid="testimonial-text" className={input} placeholder="Testimonial text (optional, max ~200 words)" value={form.text || ""} onChange={(e) => setForm({ ...form, text: e.target.value })} />

      <div className="grid sm:grid-cols-[1fr_auto] gap-3">
        <input data-testid="testimonial-video-link" className={input} placeholder="https://drive.google.com/file/d/FILE_ID/view?usp=sharing" value={form.video_link || ""} onChange={(e) => setForm({ ...form, video_link: e.target.value })} />
        <button type="button" onClick={doPreview} data-testid="testimonial-preview-btn" className={btnGhost}>
          Preview video
        </button>
      </div>
      <input data-testid="testimonial-poster" className={input} placeholder="Cover image URL (optional)" value={form.poster || ""} onChange={(e) => setForm({ ...form, poster: e.target.value })} />

      {preview && (
        <iframe src={preview} title="preview" data-testid="testimonial-preview-frame" className="w-full aspect-video rounded-xl border border-slate-200 dark:border-white/10" allow="autoplay" />
      )}

      <div className="flex items-center gap-3">
        <select data-testid="testimonial-status" className={`${input} max-w-[160px]`} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <button type="submit" disabled={busy} data-testid="testimonial-save" className={btnDark}>
          <Check className="h-3 w-3" /> {busy ? "Saving…" : "Save testimonial"}
        </button>
      </div>
    </form>
  );
}

export default function TestimonialsManager() {
  const [rows, setRows] = useState([]);
  const [tiers, setTiers] = useState({});
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await req("get", "/admin/testimonials");
    setRows(data);
  }, []);

  useEffect(() => {
    load();
    req("get", "/admin/packages").then(({ data }) => setTiers(data.tiers)).catch(() => {});
  }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.name}'s testimonial?`)) return;
    await req("delete", `/admin/testimonials/${t.id}`);
    toast.success("Testimonial deleted");
    load();
  };

  const togglePublish = async (t) => {
    await req("put", `/admin/testimonials/${t.id}`, {
      name: t.name,
      email: t.email || null,
      role: t.role,
      package_id: t.package_id,
      rating: t.rating,
      text: t.quote,
      video_link: t.video_link_raw || null,
      poster: t.poster || null,
      status: t.status === "published" ? "draft" : "published",
    });
    toast.success(t.status === "published" ? "Unpublished" : "Published to website");
    load();
  };

  return (
    <div data-testid="admin-testimonials-manager" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {rows.length} testimonials. Published ones show in the website's Success Stories section.
        </p>
        <button type="button" onClick={() => setEditing({})} data-testid="admin-add-testimonial" className={btnDark}>
          <Plus className="h-3.5 w-3.5" /> Add testimonial
        </button>
      </div>

      {editing && (
        <TestimonialForm
          initial={editing.id ? { ...editing, text: editing.quote, video_link: editing.video_link_raw } : null}
          tiers={tiers}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/[0.03] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 border-b border-slate-100 dark:border-white/5">
              {["Student", "Package", "Rating", "Video", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-400 text-sm">
                  No testimonials yet.
                </td>
              </tr>
            )}
            {rows.map((t) => (
              <tr key={t.id} data-testid={`testimonial-row-${t.id}`} className="hover:bg-slate-50 dark:hover:bg-white/5">
                <td className="px-4 py-3 font-medium">{t.name}</td>
                <td className="px-4 py-3 text-slate-500">{t.package || "—"}</td>
                <td className="px-4 py-3 text-slate-500">{t.rating}★</td>
                <td className="px-4 py-3 text-slate-500">{t.embed_url || t.video ? "Yes" : "—"}</td>
                <td className="px-4 py-3">
                  <button type="button" onClick={() => togglePublish(t)} data-testid={`testimonial-toggle-${t.id}`} className={t.status === "published" ? "text-emerald-500" : "text-amber-500"}>
                    {t.status}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 justify-end">
                    <button type="button" onClick={() => setEditing(t)} data-testid={`testimonial-edit-${t.id}`} className={btnGhost}>
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => remove(t)} data-testid={`testimonial-delete-${t.id}`} className={btnGhost}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
